import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FieldErrors } from 'react-hook-form'
import type { CountryCode } from 'libphonenumber-js/min'
import { getPhoneCountries, IPhoneCountry } from '@/constants/phoneCountries'
import { useTranslation } from '@/localization/useTranslation'
import { formatNational, parseE164, toCountryCode, toE164 } from '@/utils/phone'
import { useHighlightedField } from '../FieldHighlight/context'
import { getStatusIcon } from '../statusIcon'

import classes from './styles.module.scss'

interface IPhoneInputProps {
  name: string
  value?: string
  onChange: (value: string) => void
  onBlur?: () => void
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  errors?: FieldErrors
  error?: string
  isWarning?: boolean
  withError?: boolean
  fullWidth?: boolean
  /** Alpha-2 codes, in the order they should appear. Omitted = every country. */
  countries?: string[]
  /**
   * Alpha-2 the field starts on while the user has not chosen one. With neither
   * this nor `countries`, the field opens on the first country of the full list.
   */
  defaultCountry?: string
}

const withParsedCountry = (options: IPhoneCountry[], country?: CountryCode): IPhoneCountry[] => {
  if (!country || options.some(option => option.code === country)) return options
  // A stored number from outside the configured list stays selectable, so
  // editing another field cannot silently rewrite someone's phone country.
  // Matched by code rather than taken wholesale: `getPhoneCountries` answers an
  // unresolvable code with the entire world, and libphonenumber knows territories
  // this app has no name for — so a number from one of those replaced the
  // brand's list with ~250 duplicated entries and moved the selection, and the
  // prefix on screen, to whatever sorted first.
  const stored = getPhoneCountries([country]).find(option => option.code === country)
  return stored ? [stored, ...options] : options
}

/**
 * Where the caret belongs once the value has been reformatted: after the same
 * number of digits it had to its left. Counting digits rather than characters is
 * what makes it survive separators appearing and disappearing around it.
 */
const caretAfterDigits = (formatted: string, digitsBefore: number): number => {
  // A leading '+' is load-bearing, not decoration: `handleNumberChange` reads it
  // to decide the number carries its own country. Parking the caret at 0 let the
  // next digit land in front of it ('5+'), so the prefix stopped being a prefix
  // and the number was read as national against the default country.
  if (digitsBefore <= 0) return formatted.startsWith('+') ? 1 : 0
  let seen = 0
  for (let index = 0; index < formatted.length; index++) {
    if (/\d/.test(formatted[index])) {
      seen++
      if (seen === digitsBefore) return index + 1
    }
  }
  return formatted.length
}

const countDigits = (value: string) => (value.match(/\d/g) ?? []).length

const PhoneInput = forwardRef((props: IPhoneInputProps, ref: React.Ref<HTMLInputElement>) => {
  const {
    name,
    value = '',
    onChange,
    onBlur,
    label,
    placeholder,
    required = false,
    disabled = false,
    errors,
    error,
    isWarning = false,
    withError = true,
    fullWidth,
    countries,
    defaultCountry,
  } = props
  const { t } = useTranslation()

  const parsed = parseE164(value)
  const [pickedCountry, setPickedCountry] = useState<CountryCode | undefined>(undefined)
  const [digits, setDigits] = useState(parsed.nationalNumber)
  const [emittedValue, setEmittedValue] = useState(value)

  // Prop-synced copy: only a change coming from outside (form reset, defaults
  // arriving late) rewrites the typed digits — never our own emission.
  if (value !== emittedValue) {
    setEmittedValue(value)
    setDigits(parsed.nationalNumber)
    // The incoming number carries its own country; a pin from before the change
    // would keep the old prefix on screen over the new digits.
    setPickedCountry(undefined)
  }

  const options = withParsedCountry(getPhoneCountries(countries), parsed.country)
  const requested = pickedCountry ?? parsed.country ?? toCountryCode(defaultCountry)
  // Clamped to the list on purpose, and the single source for both what is shown
  // and what is emitted: a country outside it has no dial code on screen and, in
  // a one-country config, no selector to correct it — emitting with it would
  // submit a prefix the field never displayed.
  const selected = options.find(option => option.code === requested) ?? options[0]
  const hasSelector = options.length > 1
  // While the field holds a half-typed international number it owns the prefix,
  // so the dial code beside it would read as a second one.
  const isInternationalInput = digits.startsWith('+')

  const formErrorMessage = errors?.[name]?.message as string | undefined
  // Keyed on the error itself, not its text: a rule with no configured message
  // would otherwise block the submit with nothing on screen to explain it.
  const hasError = !!errors?.[name] || !!error
  const highlightedField = useHighlightedField()
  // A field the URL points at borrows the warning look rather than inventing one.
  const hasWarning = isWarning || highlightedField === name
  const statusIcon = getStatusIcon({ isError: hasError, isWarning: hasWarning })

  const inputRef = useRef<HTMLInputElement | null>(null)
  // Set while a keystroke is in flight; consumed by the layout effect below.
  const pendingCaretRef = useRef<number | null>(null)

  // The input is controlled and the displayed value is re-formatted on every
  // keystroke, so React rewrites it wholesale and the caret lands at the end —
  // editing in the middle of a number was impossible, and deleting a separator
  // looked like nothing happened because the digits did not change.
  useLayoutEffect(() => {
    const digitsBefore = pendingCaretRef.current
    pendingCaretRef.current = null
    const input = inputRef.current
    if (digitsBefore === null || !input) return
    const caret = caretAfterDigits(input.value, digitsBefore)
    input.setSelectionRange(caret, caret)
  })

  const attachInput = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
  }, [ref])

  const emit = (nextDigits: string, nextCountry: CountryCode) => {
    const e164 = toE164(nextDigits, nextCountry)
    setEmittedValue(e164)
    onChange(e164)
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    pendingCaretRef.current = countDigits(raw.slice(0, e.target.selectionStart ?? raw.length))
    const nextDigits = raw.replace(/\D/g, '')

    // An international number arrives either in one paste or a character at a
    // time, and carries its own country — re-prefixing it would double it. The
    // leading '+' is kept in `digits` rather than tracked in a flag of its own:
    // a flag has to survive the controlled input wiping the character it stands
    // for, and has to be released again at exactly the right moment. The string
    // is the state, so neither problem exists.
    if (raw.trimStart().startsWith('+')) {
      const international = parseE164(`+${nextDigits}`)
      if (international.country) {
        setPickedCountry(international.country)
        setDigits(international.nationalNumber)
        emit(international.nationalNumber, international.country)
        return
      }

      // Not a whole number yet. The partial is emitted as typed, never as '':
      // both consumers validate with `!value || isValidPhone(value)`, so an
      // empty emission reads as "left blank" and passes — the half-typed number
      // would sit on screen while the form saved nothing and reported success.
      // Emitting it keeps `isValidPhone` in charge, and keeps `emittedValue`
      // equal to what the parent holds so a later reset still rewrites the field.
      const partial = `+${nextDigits}`
      setDigits(partial)
      setEmittedValue(partial)
      onChange(partial)
      return
    }

    setDigits(nextDigits)
    // Typing pins the country: a default resolving late must not re-prefix a
    // number the user already started.
    setPickedCountry(selected.code)
    emit(nextDigits, selected.code)
  }

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextCountry = toCountryCode(e.target.value)
    if (!nextCountry) return
    setPickedCountry(nextCountry)
    emit(digits, nextCountry)
  }

  return (
    <div className={classNames({
      [classes.container]: true,
      [classes.fullWidth]: fullWidth,
      [classes.withError]: withError,
      [classes.hasWarning]: hasWarning,
      [classes.hasError]: hasError,
    })}>
      {label &&
        <label
          className={classNames({
            [classes.label]: true,
            [classes.label__disabled]: disabled,
          })}
          htmlFor={name}
        >
          {label + (required ? ' *' : '')}
        </label>
      }
      <div className={classes.field}>
        {isInternationalInput ? null : hasSelector
          ? (
            <div className={classes.country}>
              <select
                className={classes.country__select}
                value={selected.code}
                onChange={handleCountryChange}
                disabled={disabled}
                aria-label={t('Country code')}
                data-testid="phone-input-country"
              >
                {options.map(option => (
                  <option key={option.code} value={option.code}>
                    {`${t(option.name)} (${option.dialCode})`}
                  </option>
                ))}
              </select>
              <span className={classes.country__code} aria-hidden>{selected.dialCode}</span>
            </div>
          )
          : <span className={classes.country__code}>{selected.dialCode}</span>
        }
        <input
          className={classes.number}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          id={name}
          name={name}
          ref={attachInput}
          value={isInternationalInput ? digits : formatNational(digits, selected.code)}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleNumberChange}
          onBlur={onBlur}
          data-testid="phone-input-number"
        />
        {statusIcon && <FontAwesomeIcon icon={statusIcon} className={classes.statusIcon} />}
      </div>
      {(hasError && withError) && (
        <span className={classes.error}>{formErrorMessage || error}</span>
      )}
    </div>
  )
})

PhoneInput.displayName = 'PhoneInput'

export default PhoneInput
